import { Chip, Stack, Typography } from '@mui/material';

import { getPrimaryDocument } from '~/components/contractReview/detailPage/contractDocuments';
import { ShowDocumentCell } from '~/components/rowPreview/ShowDocumentCell';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { ContractTypeOptions } from '~/data/schemas/contract';

import {
  contractCarCount,
  contractMonthlyPrice,
  contractTypeLabel,
  daysUntil,
  displayDate,
  formatCurrency,
} from './contractDetailShared';
import type { ContractReview } from './types';

const renewalColor = (days: number): 'default' | 'warning' | 'error' => {
  if (days < 0) return 'error';
  if (days <= 120) return 'warning';
  return 'default';
};

/**
 * The Contracts grid's column set, shared by the Contracts page and the invoice detail
 * page's Governing Contract section so a contract row reads the same wherever it turns
 * up. Generic over the row so a caller can carry extra fields alongside the review view
 * model; every column here reads only `ContractReview` fields.
 */
export function getContractsTableColumns<TRow extends ContractReview = ContractReview>({
  currencyCode,
  vehiclesLabel,
  onPreviewDocument,
}: {
  /** From `UserContext`. Required rather than defaulted to `'USD'`: a caller that
   *  forgets shows a GBP group `$`, and nothing in a typecheck or a test would say so. */
  currencyCode: string;
  /** `useLocaleTerms().vehicles` — "Cars" in en-US, "Lifts" in en-GB. Required for the
   *  same reason. */
  vehiclesLabel: string;
  /** Puts a "Show document" link under the name of a contract with one on file. */
  onPreviewDocument?: (row: TRow) => void;
}): LiftAIGridColDef<TRow>[] {
  return [
    {
      field: 'name',
      headerName: 'Contract',
      flex: 2,
      minWidth: 220,
      ...(onPreviewDocument && {
        renderCell: ({ row }: { row: TRow }) => (
          <ShowDocumentCell
            onShowDocument={getPrimaryDocument(row) ? () => onPreviewDocument(row) : undefined}
          >
            {row.name}
          </ShowDocumentCell>
        ),
      }),
    },
    {
      // A column of its own rather than a caption under the name: it is a small set of known
      // values, so as a field it sorts and gets a pick-list filter. Out-of-enum and empty
      // types still render, they just have no entry in that list.
      field: 'type',
      headerName: 'Type',
      type: 'singleSelect',
      width: 180,
      valueOptions: Object.entries(ContractTypeOptions).map(([value, label]) => ({
        value,
        label,
      })),
      valueFormatter: (value: string) => contractTypeLabel(value),
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'properties',
      headerName: 'Properties',
      width: 130,
      filterable: false,
      sortable: false,
      valueGetter: (_, row) => row.properties.length,
      renderCell: ({ row }) => {
        const first = row.properties[0]?.name ?? '—';
        return (
          <Stack>
            <Typography variant="body2">
              {row.properties.length} {row.properties.length === 1 ? 'property' : 'properties'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {first}
              {row.properties.length > 1 ? ` +${row.properties.length - 1}` : ''}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'carCount',
      headerName: vehiclesLabel,
      type: 'number',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_, row) => contractCarCount(row),
    },
    {
      field: 'startDate',
      headerName: 'Term',
      flex: 1,
      minWidth: 200,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack>
          <Typography variant="body2">
            {displayDate(row.startDate)} → {displayDate(row.expirationDate)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.term}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'daysToRenewal',
      headerName: 'Renews',
      type: 'number',
      width: 130,
      valueGetter: (_, row) => daysUntil(row.expirationDate),
      renderCell: ({ value }) => {
        // No expiration date on the contract → no renewal countdown (AMP-585).
        if (value === null || value === undefined) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const days = value as number;
        const color = renewalColor(days);
        const label = days < 0 ? `${Math.abs(days)}d ago` : `${days}d`;
        return (
          <Chip
            size="small"
            color={color}
            label={label}
            variant={color === 'default' ? 'outlined' : 'filled'}
          />
        );
      },
    },
    {
      field: 'monthlyPrice',
      headerName: 'Monthly',
      type: 'number',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_, row) => contractMonthlyPrice(row),
      valueFormatter: (value: number) => formatCurrency({ value, currencyCode }),
    },
  ];
}
